// src/components/ExternalForfeoRedirectModal.tsx
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaArrowRight, FaExternalLinkAlt, FaShieldAlt, FaTimes, FaUndoAlt } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { FORFEO_COM_URL } from '../lib/forfeoExternalLinks';

type ExternalForfeoRedirectModalProps = {
  isOpen: boolean;
  onClose: () => void;
  targetUrl: string;
  title?: string;
  description?: string;
  contextLabel?: string;
};

export default function ExternalForfeoRedirectModal({
  isOpen,
  onClose,
  targetUrl,
  title,
  description,
  contextLabel,
}: ExternalForfeoRedirectModalProps) {
  const { i18n } = useTranslation();
  const isEn = String(i18n.language || '').toLowerCase().startsWith('en');
  const safeTargetUrl = targetUrl || `${FORFEO_COM_URL}/carte-cadeau`;

  const copy = {
    title: title || (isEn ? 'You are leaving Forfeo Explore' : 'Vous quittez Forfeo Explore'),
    eyebrow: isEn ? 'Secure Forfeo bridge' : 'Passerelle sécurisée Forfeo',
    description:
      description ||
      (isEn
        ? 'You will be redirected to forfeo.com, Forfeo’s official transactional storefront, to complete the purchase of your virtual Forfeo card.'
        : 'Vous allez être redirigé vers forfeo.com, la vitrine transactionnelle officielle de Forfeo, pour finaliser l’achat de votre carte virtuelle.'),
    note: isEn
      ? 'You can come back to Forfeo Explore anytime to discover experiences, plan your stay or use your traveler space.'
      : 'Vous pourrez revenir sur Forfeo Explore à tout moment pour découvrir des expériences, planifier votre séjour ou utiliser votre espace voyageur.',
    continue: isEn ? 'Continue to forfeo.com' : 'Continuer vers forfeo.com',
    stay: isEn ? 'Stay on Forfeo Explore' : 'Rester sur Forfeo Explore',
    newTab: isEn ? 'Open in a new tab' : 'Ouvrir dans un nouvel onglet',
    destination: isEn ? 'Destination' : 'Destination',
  };

  const handleContinue = () => {
    window.location.href = safeTargetUrl;
  };

  const handleNewTab = () => {
    window.open(safeTargetUrl, '_blank', 'noopener,noreferrer');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/70 backdrop-blur-xl p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          role="dialog"
          aria-modal="true"
          aria-label={copy.title}
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.96 }}
            transition={{ duration: 0.22 }}
            className="relative w-full max-w-xl overflow-hidden rounded-[2rem] border border-white/10 bg-white shadow-2xl"
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-slate-500 shadow-sm transition hover:bg-slate-100 hover:text-slate-900"
              aria-label={isEn ? 'Close' : 'Fermer'}
            >
              <FaTimes />
            </button>

            <div className="relative overflow-hidden bg-slate-950 px-7 py-8 text-white">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,rgba(59,130,246,0.45),transparent_32%),radial-gradient(circle_at_90%_20%,rgba(34,211,238,0.28),transparent_32%)]" />
              <div className="relative">
                <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[10px] font-black uppercase tracking-[0.22em] text-cyan-100">
                  <FaShieldAlt className="text-cyan-300" /> {copy.eyebrow}
                </div>
                <h2 className="text-3xl font-black tracking-tight md:text-4xl">{copy.title}</h2>
                <p className="mt-4 max-w-lg text-sm font-semibold leading-relaxed text-slate-300 md:text-base">{copy.description}</p>
              </div>
            </div>

            <div className="space-y-5 p-7">
              <div className="rounded-3xl border border-blue-100 bg-blue-50 p-5">
                <p className="text-[10px] font-black uppercase tracking-[0.22em] text-blue-700">{copy.destination}</p>
                <p className="mt-2 text-lg font-black text-slate-950">{contextLabel || 'forfeo.com'}</p>
                <p className="mt-1 break-all text-xs font-bold text-blue-700">{safeTargetUrl}</p>
              </div>

              <div className="rounded-3xl border border-slate-100 bg-slate-50 p-5 text-sm font-semibold leading-relaxed text-slate-600">
                {copy.note}
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={handleContinue}
                  className="inline-flex items-center justify-center gap-3 rounded-2xl bg-slate-950 px-5 py-4 text-xs font-black uppercase tracking-widest text-white shadow-xl transition hover:-translate-y-0.5 hover:bg-blue-700"
                >
                  {copy.continue} <FaArrowRight />
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 text-xs font-black uppercase tracking-widest text-slate-800 transition hover:-translate-y-0.5 hover:bg-slate-50"
                >
                  <FaUndoAlt /> {copy.stay}
                </button>
              </div>

              <button
                type="button"
                onClick={handleNewTab}
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-100 px-4 py-3 text-xs font-black uppercase tracking-widest text-slate-600 transition hover:bg-slate-200"
              >
                <FaExternalLinkAlt /> {copy.newTab}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
